import api from '@/lib/api'
import type { Prediction, PredictionRequest, EntityListResponse, EntityFeaturesResponse } from '@/types'
import { type ModelId, type PredictionId, unbrand } from '@/types/branded'
import type { APIErrorResponse } from '@/types/errors'
import { extractAPIError } from '@/lib/error-utils'

/**
 * Typed result wrapper for service operations.
 * Enables discriminated union pattern for error handling.
 */
export type Result<T, E = APIErrorResponse> =
  | { success: true; data: T }
  | { success: false; error: E }

export interface PredictionResponse {
  request_id: string
  entity_id: string
  prediction: number
  prediction_label?: string
  confidence: number
  risk_score: number
  explanation?: {
    summary: string
    risk_level: string
    top_factors: Array<{
      feature: string
      value: number | string
      contribution: number
      direction: string
      description: string
    }>
  }
  model_id: string
  model_version: string  // Matches backend field name
  latency_ms: number
}

export interface BatchPredictionResponse {
  batch_id: string
  status: string
  total: number
  processed: number
  failed: number
}

export interface BatchStatusResponse {
  batch_id: string
  model_id: string
  status: string
  total: number
  processed: number
  failed: number
  created_at: string
}

export const predictionsService = {
  /**
   * Make a real-time prediction.
   */
  async predict(request: PredictionRequest): Promise<Result<PredictionResponse>> {
    try {
      const { data } = await api.post<PredictionResponse>('/predictions/realtime', request)
      return { success: true, data }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * Make a real-time prediction (alias for predict).
   */
  async realTimePredict(request: PredictionRequest): Promise<Result<PredictionResponse>> {
    return this.predict(request)
  },

  /**
   * Trigger batch predictions for multiple entities.
   */
  async batchPredict(params: {
    model_id: ModelId
    entity_ids: string[]
    include_explanation?: boolean
  }): Promise<Result<BatchPredictionResponse>> {
    try {
      const { data } = await api.post<BatchPredictionResponse>('/predictions', {
        ...params,
        model_id: unbrand(params.model_id),
      })
      return { success: true, data }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * List predictions with optional filtering.
   */
  async list(params?: {
    model_id?: ModelId
    entity_id?: string
    risk_level?: string
    page?: number
    page_size?: number
  }): Promise<Result<{ items: Prediction[]; total: number; page: number; page_size: number }>> {
    try {
      const requestParams = params ? {
        ...params,
        model_id: params.model_id ? unbrand(params.model_id) : undefined,
      } : undefined

      const { data } = await api.get<{ items: Prediction[]; total: number; page: number; page_size: number }>(
        '/predictions',
        { params: requestParams }
      )
      return { success: true, data }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * Get a single prediction by ID.
   */
  async get(id: PredictionId): Promise<Result<Prediction>> {
    try {
      const { data } = await api.get<Prediction>(`/predictions/${unbrand(id)}`)
      return { success: true, data }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * Get high-risk predictions above a threshold.
   */
  async getHighRisk(threshold: number = 0.7, limit: number = 10): Promise<Result<{ predictions: Prediction[]; total: number }>> {
    try {
      const { data } = await api.get<{ predictions: Prediction[]; total: number }>(
        '/predictions',
        {
          params: {
            min_score: threshold,
            limit,
            sort: '-prediction_value',
          },
        }
      )
      return { success: true, data }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * List entities available for prediction.
   */
  async listEntities(params: {
    model_id: ModelId
    search?: string
    page?: number
    page_size?: number
  }): Promise<Result<EntityListResponse>> {
    try {
      const { data } = await api.get<EntityListResponse>(
        `/predictions/${unbrand(params.model_id)}/entities`,
        {
          params: {
            search: params.search,
            page: params.page,
            page_size: params.page_size,
          },
        }
      )
      return { success: true, data }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * Get feature values for a specific entity.
   */
  async getEntityFeatures(params: {
    model_id: ModelId
    entity_id: string
  }): Promise<Result<EntityFeaturesResponse>> {
    try {
      const { data } = await api.get<EntityFeaturesResponse>(
        `/predictions/${unbrand(params.model_id)}/features/${params.entity_id}`
      )
      return { success: true, data }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * Trigger batch predictions for all entities in a model.
   * Accepts either branded ModelId or plain string for flexibility.
   */
  async batchPredictAll(params: {
    model_id: ModelId | string
    include_explanation?: boolean
  }): Promise<Result<{ batch_id: string; status: string; total_entities: number }>> {
    try {
      // Build request body explicitly to avoid sending undefined values
      // which serialize to null and fail Pydantic validation
      const requestBody: { model_id: string; include_explanation?: boolean } = {
        model_id: typeof params.model_id === 'string' ? params.model_id : unbrand(params.model_id),
      }
      if (params.include_explanation !== undefined) {
        requestBody.include_explanation = params.include_explanation
      }

      const { data } = await api.post<{ batch_id: string; status: string; total_entities: number }>(
        '/predictions/batch/all',
        requestBody
      )
      return { success: true, data }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * Get prediction history with optional filters.
   */
  async getHistory(params: {
    model_id?: ModelId
    entity_id?: string
    risk_level?: 'high' | 'medium' | 'low'
    date_from?: string
    date_to?: string
    page?: number
    limit?: number
  }): Promise<Result<unknown>> {
    try {
      const requestParams = {
        ...params,
        model_id: params.model_id ? unbrand(params.model_id) : undefined,
      }
      const { data } = await api.get('/predictions/history/list', { params: requestParams })
      return { success: true, data }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * Get the status of a batch prediction job.
   *
   * Used to poll a submitted batch until it completes so the results
   * become downloadable.
   */
  async getBatchStatus(batchId: string): Promise<Result<BatchStatusResponse>> {
    try {
      const { data } = await api.get<BatchStatusResponse>(`/predictions/batch/${batchId}`)
      return { success: true, data }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * Download the results of a single completed batch as CSV or JSON.
   *
   * Streams from GET /predictions/batch/{batch_id}/export, which returns only
   * the predictions tied to that batch_id.
   */
  async exportBatch(params: {
    batchId: string
    format: 'csv' | 'json'
  }): Promise<Result<Blob>> {
    try {
      const response = await api.get(`/predictions/batch/${params.batchId}/export`, {
        params: { format: params.format },
        responseType: 'blob',
      })
      return { success: true, data: response.data }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * Export predictions to CSV or JSON.
   */
  async exportPredictions(params: {
    model_id?: ModelId
    format: 'csv' | 'json'
    filters?: {
      model_id?: ModelId
      entity_id?: string
      risk_level?: 'high' | 'medium' | 'low'
      date_from?: string
      date_to?: string
    }
  }): Promise<Result<Blob>> {
    try {
      const requestParams = {
        ...params,
        model_id: params.model_id ? unbrand(params.model_id) : undefined,
        filters: params.filters ? {
          ...params.filters,
          model_id: params.filters.model_id ? unbrand(params.filters.model_id) : undefined,
        } : undefined,
      }
      const response = await api.post('/predictions/export', requestParams, {
        responseType: 'blob',
      })
      return { success: true, data: response.data }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },
}
